import type { Ecc } from "./encode.ts";
import type { QrStyle } from "./style.ts";

/**
 * The things that make a pretty code an unreadable one. A scanner is not a
 * person: it thresholds the image, looks for three finder patterns in a quiet
 * field, and samples the middle of each module. Each check below is one of the
 * ways a design quietly breaks that, and the app says so before the download
 * rather than after the poster is printed.
 */

export interface Warning {
  id: string;
  text: string;
}

export function scanWarnings(style: QrStyle, ecc: Ecc): Warning[] {
  const out: Warning[] = [];
  const fg = luminance(style.fill.color);
  const bg = style.background ? luminance(style.background) : 1; // assume paper
  const ratio = contrastRatio(fg, bg);

  if (ratio < 3) {
    out.push({
      id: "contrast",
      text: "The code and its background are too close in brightness for a camera to separate. Darken one or lighten the other.",
    });
  }
  if (fg > bg) {
    out.push({
      id: "inverted",
      text: "Light code on a dark background. Many scanners read it, some older ones only ever look for dark on light.",
    });
  }
  if (style.margin < 2) {
    out.push({
      id: "quiet-zone",
      text: "A code needs empty space around it — four modules is the standard. With less, a scanner can miss where the code ends.",
    });
  }
  if (style.eyes === "circle" || style.eyes === "leaf") {
    out.push({
      id: "eyes",
      // Measured, not guessed: every module style decodes with OpenCV's
      // detector, but a round or leaf-shaped finder fails it outright, because
      // a finder is looked for as a 1:1:3:1:1 run of dark and light along a
      // scan line and a circle only gives that through its middle. Phone
      // cameras are far more forgiving; cheaper hardware scanners are not.
      text: "Round corner markers are the part a scanner uses to find the code at all. Phone cameras usually cope; older and till-side scanners often do not. Try it before you print it.",
    });
  }
  if (style.logo && style.logo.scale > 0.22 && ecc !== "H") {
    out.push({
      id: "logo",
      text: "That logo covers a lot of the code. Raise error correction to High so the missing part can be reconstructed.",
    });
  }
  return out;
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function luminance(hex: string): number {
  const [r, g, b] = rgb(hex);
  const channel = (value: number) => {
    const v = value / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(a: number, b: number): number {
  const light = Math.max(a, b);
  const dark = Math.min(a, b);
  return (light + 0.05) / (dark + 0.05);
}

/** #abc and #aabbcc; anything else reads as black, which warns rather than throws. */
function rgb(hex: string): [number, number, number] {
  const value = hex.trim().replace(/^#/, "");
  const full =
    value.length === 3
      ? value
          .split("")
          .map((c) => c + c)
          .join("")
      : value;
  if (!/^[0-9a-f]{6}$/i.test(full)) return [0, 0, 0];
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}
